import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  UserPlus, Users, Mail, Phone, Building2, ToggleLeft, ToggleRight, 
  Trash2, MessageSquare, Eye, ExternalLink, Copy, Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientPortalManagement = () => {
  const { token } = useAuth();
  const [portalUsers, setPortalUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  
  const [formData, setFormData] = useState({
    client_id: '',
    email: '',
    password: '',
    name: '',
    mobile: ''
  });
  const [formLoading, setFormLoading] = useState(false);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [portalRes, clientsRes] = await Promise.all([
        axios.get(`${API}/client-portal/users`, axiosConfig),
        axios.get(`${API}/clients`, axiosConfig)
      ]);
      setPortalUsers(portalRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePortalUser = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      await axios.post(`${API}/client-portal/auth/register`, formData, axiosConfig);
      setShowCreateModal(false);
      setFormData({ client_id: '', email: '', password: '', name: '', mobile: '' });
      fetchData();
      alert('Client portal account created successfully!');
    } catch (error) {
      console.error('Error creating portal user:', error);
      alert(error.response?.data?.detail || 'Failed to create portal account');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await axios.put(`${API}/client-portal/users/${userId}/toggle-status`, {}, axiosConfig);
      fetchData();
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Failed to update status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this portal account?')) return;
    
    try {
      await axios.delete(`${API}/client-portal/users/${userId}`, axiosConfig);
      fetchData();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete portal account');
    }
  };

  const handleViewMessages = async (clientId) => {
    setSelectedClient(clientId);
    try {
      const response = await axios.get(`${API}/client-portal/messages/client/${clientId}`, axiosConfig);
      setMessages(response.data);
      setShowMessagesModal(true);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedClient) return;
    
    try {
      await axios.post(`${API}/client-portal/messages/send/${selectedClient}`, 
        { message: newMessage }, 
        axiosConfig
      );
      setNewMessage('');
      const response = await axios.get(`${API}/client-portal/messages/client/${selectedClient}`, axiosConfig);
      setMessages(response.data);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    }
  };

  const copyPortalLink = () => {
    const link = `${window.location.origin}/client-portal`;
    navigator.clipboard.writeText(link);
    setCopiedId('link');
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Get clients without portal accounts
  const clientsWithoutPortal = clients.filter(
    client => !portalUsers.find(pu => pu.client_id === client.id)
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            Client Portal Management
          </h2>
          <p className="text-gray-600 mt-2">Manage client portal access and communicate with clients</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={copyPortalLink}
            className="border-2 border-blue-200 text-blue-600 hover:bg-blue-50"
            data-testid="copy-portal-link-btn"
          >
            {copiedId === 'link' ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            Copy Portal Link
          </Button>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            data-testid="create-portal-user-btn"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            Create Portal Account
          </Button>
        </div>
      </div>

      {/* Portal Link Info */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <ExternalLink className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900">Client Portal URL</h3>
              <p className="text-sm text-blue-700">
                Share this link with your clients: <code className="bg-blue-100 px-2 py-1 rounded">{window.location.origin}/client-portal</code>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0">
          <CardContent className="p-6 text-center">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <div className="text-3xl font-bold">{portalUsers.length}</div>
            <div className="text-sm opacity-80">Portal Accounts</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
          <CardContent className="p-6 text-center">
            <ToggleRight className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <div className="text-3xl font-bold">{portalUsers.filter(u => u.is_active).length}</div>
            <div className="text-sm opacity-80">Active Accounts</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0">
          <CardContent className="p-6 text-center">
            <Building2 className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <div className="text-3xl font-bold">{clientsWithoutPortal.length}</div>
            <div className="text-sm opacity-80">Clients Without Portal</div>
          </CardContent>
        </Card>
      </div>

      {/* Portal Users List */}
      <Card>
        <CardHeader>
          <CardTitle>Portal Users</CardTitle>
          <CardDescription>Clients with portal access</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse"></div>
              ))}
            </div>
          ) : portalUsers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No Portal Users Yet</h3>
              <p className="text-gray-500 mb-4">Create portal accounts for your clients to give them access</p>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-orange-500 to-red-500"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Create First Account
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {portalUsers.map(user => (
                <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-red-100 rounded-full flex items-center justify-center">
                      <span className="text-xl font-bold text-orange-600">
                        {user.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{user.name}</div>
                      <div className="text-sm text-gray-500">{user.organization_name || user.client_name}</div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </span>
                        {user.mobile && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {user.mobile}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewMessages(user.client_id)}
                      title="View Messages"
                      data-testid={`view-messages-${user.id}`}
                    >
                      <MessageSquare className="w-4 h-4 text-blue-600" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleStatus(user.id)}
                      title={user.is_active ? 'Deactivate' : 'Activate'}
                      data-testid={`toggle-status-${user.id}`}
                    >
                      {user.is_active ? (
                        <ToggleRight className="w-5 h-5 text-green-600" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-gray-400" />
                      )}
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteUser(user.id)}
                      title="Delete"
                      className="text-red-600 hover:text-red-800 hover:bg-red-50"
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Portal User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Portal Account</DialogTitle>
            <DialogDescription>Give a client access to their portal</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreatePortalUser} className="space-y-4">
            <div>
              <Label>Select Client *</Label>
              <Select 
                value={formData.client_id} 
                onValueChange={(value) => {
                  const client = clients.find(c => c.id === value);
                  setFormData({
                    ...formData,
                    client_id: value,
                    name: client?.name || '',
                    email: client?.email || '',
                    mobile: client?.mobile || ''
                  });
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clientsWithoutPortal.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name} - {client.organization_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {clientsWithoutPortal.length === 0 && (
                <p className="text-sm text-amber-600 mt-1">All clients already have portal accounts</p>
              )}
            </div>
            
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                placeholder="Min 6 characters"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Mobile</Label>
              <Input
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500"
                disabled={formLoading || !formData.client_id}
              >
                {formLoading ? 'Creating...' : 'Create Account'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Messages Modal */}
      <Dialog open={showMessagesModal} onOpenChange={setShowMessagesModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Messages</DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col h-[500px]">
            <div className="flex-1 overflow-y-auto space-y-4 mb-4 p-2">
              {messages.length > 0 ? (
                [...messages].reverse().map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_type === 'firm' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] p-3 rounded-lg ${
                      msg.sender_type === 'firm'
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}>
                      <div className="text-sm font-medium mb-1">{msg.sender_name}</div>
                      <div>{msg.message}</div>
                      <div className={`text-xs mt-1 ${
                        msg.sender_type === 'firm' ? 'text-orange-200' : 'text-gray-500'
                      }`}>
                        {new Date(msg.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">No messages yet</p>
              )}
            </div>
            
            <div className="flex gap-2 border-t pt-4">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1"
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                className="bg-gradient-to-r from-orange-500 to-red-500"
              >
                Send
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientPortalManagement;
